import React, { useState } from "react";
import { doc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const ROLE_CONFIG = {
  parent: {
    label: "Parent",
    accent: "green",
    icon: "👨‍👩‍👧‍👦",
    description: "Refer a parent who could benefit from our supportive neurodiversity community and resources.",
    fields: [],
  },
  professional: {
    label: "Therapist/Professional",
    accent: "pink",
    icon: "🩺",
    description: "Refer a therapist or professional to help them connect with families and centers in need.",
    fields: [
      // Optionally add profession-specific fields here
    ],
  },
  organization: {
    label: "Organization",
    accent: "blue",
    icon: "🏢",
    description: "Refer a center, school, or organization to expand their reach and impact.",
    fields: [
      // Optionally add org-specific fields here
    ],
  },
};
const ROLES = [
  { key: "parent", label: "Parent" },
  { key: "professional", label: "Therapist/Professional" },
  { key: "organization", label: "Organization" },
];

export default function ReferralModal({ open, onClose, defaultRole }) {
  const [form, setForm] = useState({
    referrerName: "",
    referredName: "",
    referredEmail: "",
    referredMobile: "",
    referredRole: defaultRole || "parent",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await addDoc(collection(db, "referrals"), {
        referrerName: form.referrerName,
        referredName: form.referredName,
        referredEmail: form.referredEmail,
        referredMobile: form.referredMobile,
        referredRole: form.referredRole,
        createdAt: serverTimestamp(),
      });
      setSuccess(true);
    } catch (err) {
      setError("Could not submit referral. Please try again.");
    }
    setLoading(false);
  };

  const handleClose = () => {
    setSuccess(false);
    setError("");
    setForm({
      referrerName: "",
      referredName: "",
      referredEmail: "",
      referredMobile: "",
      referredRole: defaultRole || "parent",
    });
    onClose();
  };

  if (!open) return null;

   const roleConf = ROLE_CONFIG[form.referredRole] || ROLE_CONFIG.parent;
   return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-white p-4">
      <div className={`relative rounded-2xl shadow-2xl w-full max-w-lg animate-fadeInUp bg-white border-4 border-${roleConf.accent}-200 text-black overflow-y-auto max-h-[90vh]`}>
        <button 
          onClick={handleClose} 
          className="absolute top-3 right-4 text-gray-700 hover:text-gray-900 text-3xl font-bold z-10 bg-white rounded-full w-10 h-10 flex items-center justify-center shadow-md"
          aria-label="Close"
        >&times;</button>
        {success ? (
          <div className="flex flex-col items-center justify-center py-16 px-6">
            <span className="text-5xl mb-4">🎉</span>
            <h3 className="text-2xl font-bold mb-2 text-green-700">Thank you for your referral!</h3>
            <p className="text-gray-700 text-center mb-4">We'll reach out to them soon with an invite to join asdflo.</p>
            <button onClick={handleClose} className="px-8 py-3 bg-indigo-600 text-white rounded-lg font-semibold mt-2 text-lg">Close</button>
          </div>
        ) : (
          <form className="space-y-5 px-6 py-8" onSubmit={handleSubmit}>
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Who are you referring?</label>
              <select 
                name="referredRole" 
                value={form.referredRole} 
                onChange={handleChange} 
                required 
                className={`block w-full rounded-md border-2 border-${roleConf.accent}-300 shadow-sm focus:border-${roleConf.accent}-500 focus:ring-${roleConf.accent}-400 sm:text-sm py-2.5`}
              >
                {ROLES.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className={`flex items-center gap-3 mb-2`}>
              <span className={`text-3xl md:text-4xl drop-shadow`}>{roleConf.icon}</span>
              <div>
                <h3 className={`text-xl md:text-2xl font-bold text-${roleConf.accent}-700 mb-1`}>Refer a {roleConf.label}</h3>
                <div className="text-gray-700 text-sm md:text-base font-medium leading-tight">{roleConf.description}</div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">Your Name (optional)</label>
              <input type="text" name="referrerName" value={form.referrerName} onChange={handleChange} className={`mt-1 block w-full rounded-md border border-${roleConf.accent}-300 shadow-sm focus:border-${roleConf.accent}-500 focus:ring-${roleConf.accent}-400 sm:text-sm`} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">{roleConf.label}'s Name</label>
              <input type="text" name="referredName" value={form.referredName} onChange={handleChange} required className={`mt-1 block w-full rounded-md border border-${roleConf.accent}-300 shadow-sm focus:border-${roleConf.accent}-500 focus:ring-${roleConf.accent}-400 sm:text-sm`} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">{roleConf.label}'s Email</label>
              <input type="email" name="referredEmail" value={form.referredEmail} onChange={handleChange} required className={`mt-1 block w-full rounded-md border border-${roleConf.accent}-300 shadow-sm focus:border-${roleConf.accent}-500 focus:ring-${roleConf.accent}-400 sm:text-sm`} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">{roleConf.label}'s Mobile</label>
              <input type="tel" name="referredMobile" value={form.referredMobile} onChange={handleChange} required className={`mt-1 block w-full rounded-md border border-${roleConf.accent}-300 shadow-sm focus:border-${roleConf.accent}-500 focus:ring-${roleConf.accent}-400 sm:text-sm`} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700">Role</label>
              <select name="referredRole" value={form.referredRole} onChange={handleChange} required className={`mt-1 block w-full rounded-md border border-${roleConf.accent}-300 shadow-sm focus:border-${roleConf.accent}-500 focus:ring-${roleConf.accent}-400 sm:text-sm`}>
                {ROLES.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
            </div>
            {/* Role-specific fields can be added here if needed */}
            {error && <div className="text-red-600 text-sm font-semibold">{error}</div>}
            <button 
              type="submit" 
              disabled={loading} 
              className={`w-full py-3 px-4 ${
                form.referredRole === 'professional' 
                  ? 'bg-gray-800 hover:bg-gray-900' 
                  : `bg-${roleConf.accent}-600 hover:bg-${roleConf.accent}-700`
              } text-white rounded-lg font-bold transition disabled:opacity-60 text-lg shadow-md hover:shadow-lg`}
            >
              {loading ? "Submitting..." : `Submit Referral for ${roleConf.label}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
